import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Brain, Heart, Flame, AlertTriangle, CloudRain,
  Sparkles, Shield, Loader2, Info, Lightbulb,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emotionIcons = {
  anger: Flame, anxiety: AlertTriangle, depression: CloudRain,
  love: Heart, fear: Shield, joy: Sparkles,
};

const emotionDescriptions = {
  anger: 'Understand your anger patterns and learn healthy ways to express and channel this powerful emotion.',
  anxiety: 'Discover effective strategies to manage worry, calm your nervous system, and build resilience.',
  depression: 'Learn about the science of depression and evidence-based techniques for healing and recovery.',
  love: 'Explore the dimensions of love, build deeper connections, and nurture your relationships.',
  fear: 'Face your fears with courage. Learn to distinguish real threats from perceived ones.',
  joy: 'Cultivate lasting happiness through gratitude, meaning, and intentional positive practices.',
};

// ─── Shared Apple-style nav ─────────────────────────────────────────────
const AppleNav = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/75 border-b border-black/5">
    <div className="max-w-[1100px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <circle cx="9" cy="12" r="3.5" />
              <path d="M15 8 Q 19 6, 21 4" />
              <path d="M14.5 12 L 22 12" />
              <path d="M15 16 Q 19 18, 21 20" />
            </svg>
        </div>
        <span className="text-[17px] font-bold tracking-tight text-[#1d1d1f] lowercase">mindpulsei</span>
      </Link>
      <div className="hidden md:flex items-center gap-7">
        <Link to="/#method" className="text-[12px] text-[#424245] hover:text-[#1d1d1f]">Method</Link>
        <Link to="/emotions" className="text-[12px] text-[#1d1d1f]">Library</Link>
        <Link to="/#organizations" className="text-[12px] text-[#424245] hover:text-[#1d1d1f]">Organizations</Link>
      </div>
    </div>
  </nav>
);

// ─── List view ───────────────────────────────────────────────────────────
const EmotionsList = () => {
  useAuth();
  const [emotions, setEmotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contents, setContents] = useState({});
  const fetchedRef = React.useRef(new Set());

  const fetchEmotions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/emotions`);
      if (res.ok) {
        const data = await res.json();
        setEmotions(data.emotions);
      }
    } catch (err) {
      logger.error('Failed to fetch emotions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmotions(); }, [fetchEmotions]);

  useEffect(() => {
    emotions.forEach(async (emotion) => {
      if (fetchedRef.current.has(emotion)) return;
      fetchedRef.current.add(emotion);
      try {
        const res = await fetch(`${API}/emotions/${emotion}`);
        if (res.ok) {
          const data = await res.json();
          setContents(prev => ({ ...prev, [emotion]: data }));
        }
      } catch (err) { logger.error('Emotion content fetch failed:', err); }
    });
  }, [emotions]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('[data-reveal]');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('in-view'); io.unobserve(e.target); }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [loading]);


  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-current-teal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] antialiased">
      <AppleNav />

      {/* Hero */}
      <section className="relative pt-24 md:pt-28 pb-16 md:pb-24 text-center overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8 pt-14 md:pt-20 space-y-6" data-reveal>
          <p className="text-current-teal text-[15px] md:text-[17px] font-medium tracking-tight">Emotion Library</p>
          <h1
            style={{ fontSize: 'clamp(40px, 7vw, 96px)', lineHeight: 1, letterSpacing: '-0.035em', fontWeight: 600, maxWidth: '15ch' }}
            className="mx-auto"
            data-testid="emotions-page-title"
          >
            Understand<br />what you feel.
          </h1>
          <p className="text-[#515154] mx-auto" style={{ fontSize: 'clamp(17px, 1.5vw, 21px)', lineHeight: 1.4, letterSpacing: '-0.005em', maxWidth: '640px' }}>
            Science and knowledge-based guides for every emotion. Coping coaching with measurable results.
          </p>
        </div>
      </section>

      {/* Grid of emotion cards */}
      <section className="relative py-16 md:py-24" style={{ background: '#f5f5f7' }}>
        <div className="max-w-[1100px] mx-auto px-3 md:px-8" data-testid="emotions-list-page">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {emotions.map((emotion, i) => {
              const IconEl = emotionIcons[emotion] || Brain;
              const content = contents[emotion];
              return (
                <Link
                  key={emotion}
                  to={`/emotions/${emotion}`}
                  data-testid={`emotion-card-${emotion}`}
                  className="group rounded-[24px] overflow-hidden border border-black/5 hover:border-black/10 transition-colors bg-white flex flex-col"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="aspect-[16/10] overflow-hidden" style={{ background: '#eaeaef' }}>
                    {content?.image ? (
                      <img
                        src={content.image}
                        alt={emotion}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #eaf2ff 0%, #d6e6ff 100%)' }}>
                        <IconEl className="h-10 w-10 text-current-teal" strokeWidth={1.5} />
                      </div>
                    )}
                  </div>
                  <div className="p-6 md:p-7 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-current-teal/10">
                        <IconEl className="h-4 w-4 text-current-teal" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-[20px] font-semibold capitalize" style={{ letterSpacing: '-0.02em' }}>{emotion}</h3>
                    </div>
                    <p className="text-[#515154] text-[14px] flex-1" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>
                      {emotionDescriptions[emotion] || `Learn about ${emotion} and effective management techniques.`}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-current-teal group-hover:text-ice-mist text-[13px] font-medium">
                      Explore guide <span aria-hidden>›</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

        </div>
      </section>
    </div>
  );
};

// ─── Detail view ────────────────────────────────────────────────────────
const EmotionDetail = () => {
  const { emotion } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEmotionContent = useCallback(async () => {
    try {
      const res = await fetch(`${API}/emotions/${emotion}`);
      if (res.ok) setContent(await res.json());
      else toast.error('Emotion not found');
    } catch (err) {
      toast.error('Failed to load content');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [emotion]);

  useEffect(() => { fetchEmotionContent(); }, [fetchEmotionContent]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-current-teal" />
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-white text-[#1d1d1f]">
        <AppleNav />
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <p className="text-[#515154] mb-6 text-[15px]">Content not found.</p>
            <Link to="/emotions">
              <button className="px-6 py-3 rounded-full text-[15px] font-medium bg-current-teal hover:bg-ice-mist text-white transition-colors">
                Back to Emotion Library
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const IconEl = emotionIcons[emotion] || Brain;

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] antialiased">
      <AppleNav />

      {/* Hero */}
      <section className="relative pt-24 md:pt-28 pb-8 text-center overflow-hidden">
        {content.hero_image && (
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img src={content.hero_image} alt="" className="w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0.9) 80%, #ffffff 100%)' }} />
          </div>
        )}
        <div className="max-w-[1100px] mx-auto px-3 md:px-8 pt-14 md:pt-20 relative space-y-5" data-testid="emotion-detail-page">
          <p className="text-current-teal text-[15px] font-medium tracking-tight">Emotion Library</p>
          <div className="inline-flex items-center gap-3 justify-center">
            <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-current-teal/10">
              <IconEl className="h-5 w-5 text-current-teal" strokeWidth={1.75} />
            </div>
          </div>
          <h1 style={{ fontSize: 'clamp(38px, 6.5vw, 88px)', lineHeight: 1, letterSpacing: '-0.035em', fontWeight: 600 }}>
            {content.title}
          </h1>
          {content.description && (
            <p className="text-[#515154] mx-auto" style={{ fontSize: 'clamp(17px, 1.5vw, 21px)', lineHeight: 1.45, letterSpacing: '-0.005em', maxWidth: '680px' }}>
              {content.description}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="relative py-16 md:py-24" style={{ background: '#f5f5f7' }}>
        <div className="max-w-[1000px] mx-auto px-3 md:px-8 space-y-4 md:space-y-5">
          {content.extended_description && (
            <div className="rounded-[24px] p-8 md:p-10 bg-white border border-black/5">
              <p className="text-[#1d1d1f] text-[17px] md:text-[18px]" style={{ lineHeight: 1.55, letterSpacing: '-0.005em' }}>
                {content.extended_description}
              </p>
            </div>
          )}

          {content.did_you_know && (
            <div className="rounded-[24px] p-8 border border-black/5" style={{ background: 'linear-gradient(135deg, #eaf2ff 0%, #d6e6ff 100%)' }} data-testid="did-you-know">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-2xl flex items-center justify-center bg-current-teal/20 flex-shrink-0">
                  <Lightbulb className="h-5 w-5 text-current-teal" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-current-teal text-[13px] font-semibold tracking-tight mb-2">Did you know?</p>
                  <p className="text-[#1d1d1f] text-[15px]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>{content.did_you_know}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 md:gap-5">
            <div className="rounded-[24px] p-8 bg-white border border-black/5" data-testid="symptoms-card">
              <div className="flex items-center gap-2 mb-5">
                <Info className="h-4 w-4 text-current-teal" strokeWidth={2} />
                <h3 className="text-[15px] font-semibold tracking-tight">Common signs & symptoms</h3>
              </div>
              <ul className="space-y-3">
                {content.symptoms?.map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-current-teal" />
                    <span className="text-[14px] text-[#424245]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-[24px] p-8 bg-white border border-black/5" data-testid="causes-card">
              <div className="flex items-center gap-2 mb-5">
                <Brain className="h-4 w-4 text-current-teal" strokeWidth={2} />
                <h3 className="text-[15px] font-semibold tracking-tight">Common causes</h3>
              </div>
              <ul className="space-y-3">
                {content.causes?.map((c) => (
                  <li key={c} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-current-teal" />
                    <span className="text-[14px] text-[#424245]" style={{ lineHeight: 1.5, letterSpacing: '-0.005em' }}>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {content.management_techniques?.length > 0 && (
            <div className="rounded-[24px] p-8 md:p-10 bg-white border border-black/5" data-testid="techniques-card">
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="h-4 w-4 text-current-teal" strokeWidth={2} />
                <h3 className="text-[15px] font-semibold tracking-tight">Management techniques</h3>
              </div>
              <ul className="space-y-4">
                {content.management_techniques.map((technique, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-current-teal text-white font-semibold text-[12px] tracking-tight">
                      {index + 1}
                    </div>
                    <p className="text-[15px] text-[#1d1d1f]" style={{ lineHeight: 1.55, letterSpacing: '-0.005em' }}>{technique}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-14 md:mt-20 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          <Link to="/emotions" className="text-current-teal hover:text-ice-mist text-[15px] font-medium inline-flex items-center gap-1" data-testid="back-to-all-btn">
            All emotions <span aria-hidden>›</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

const Emotions = () => {
  const { emotion } = useParams();
  if (emotion) return <EmotionDetail />;
  return <EmotionsList />;
};

export default Emotions;
